import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Avatar,
  Switch,
  Divider,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  Globe,
  Camera,
  Save,
  Edit,
  Shield,
} from 'lucide-react'
import API from '../../utils/API'

const ProfileField = ({ label, value, icon: Icon, isEditable = false, onEdit, type = 'text' }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  
  const handleSave = () => {
    onEdit(editValue)
    setIsEditing(false)
  }
  
  const handleCancel = () => {
    setEditValue(value)
    setIsEditing(false)
  }
  
  if (isEditable && isEditing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 2 }}>
        <TextField
          label={label}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          variant="outlined"
          size="small"
          multiline={type === 'textarea'}
          rows={type === 'textarea' ? 3 : 1}
          sx={{ flex: 1 }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={handleSave} color="primary" variant="contained">
            Save
          </Button>
          <Button size="small" variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
        </Box>
      </Box>
    )
  }
  
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Icon size={16} color="gray" />
        <Box>
          <Typography variant="body2" color="grey.600" fontWeight="medium">
            {label}
          </Typography>
          <Typography variant="body1">{value || 'Not provided'}</Typography>
        </Box>
      </Box>
      {isEditable && (
        <IconButton
          size="small"
          onClick={() => setIsEditing(true)}
        >
          <Edit size={16} />
        </IconButton>
      )}
    </Box>
  )
}

const NotificationSetting = ({ title, description, isEnabled, onToggle }) => (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
        <Box>
          <Typography variant="body1" fontWeight="600" color="grey.800">{title}</Typography>
          <Typography variant="body2" color="grey.600">
            {description}
          </Typography>
        </Box>
        <Switch
          checked={isEnabled}
          onChange={onToggle}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: 'primary.main',
              '& + .MuiSwitch-track': {
                backgroundColor: 'primary.main',
              },
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: 'primary.main',
            },
          }}
        />
      </Box>
    )

export default function Profile() {
      const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
      const [loading, setLoading] = useState(true)
      const [profile, setProfile] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        website: '',
        bio: '',
        location: '',
        avatar: null,
      })
      
      const [notificationSettings, setNotificationSettings] = useState({
        emailNotifications: true,
        smsNotifications: false,
        orderUpdates: true,
        eventReminders: true,
        marketingEmails: false,
        securityAlerts: true,
      })
      
      useEffect(() => {
        fetchProfileData()
      }, [])
      
      const fetchProfileData = async () => {
        try {
          setLoading(true)
          
          // Get showrunner profile data
          const authResponse = await API.getAuth()
          if (!authResponse.data.showrunner?.id) {
            console.error('No showrunner ID found in auth response')
            return
          }
          
          const showrunnerId = authResponse.data.showrunner.id
          
          const profileResponse = await API.getShowRunner(showrunnerId)
          const profileData = profileResponse.data
          
          setProfile({
            firstName: profileData.first_name || '',
            lastName: profileData.last_name || '',
            email: profileData.email || '',
            phone: profileData.phone || '',
            company: profileData.company || '',
            website: profileData.website || '',
            bio: profileData.bio || '',
            location: profileData.location || '',
            avatar: profileData.avatar || null,
          })
          
          // Fetch notification preferences
          const notificationResponse = await API.getTicketSalesNotificationPreferences(showrunnerId)
          if (notificationResponse.data) {
            setNotificationSettings({
              emailNotifications: notificationResponse.data.email_notifications || true,
              smsNotifications: notificationResponse.data.sms_notifications || false,
              orderUpdates: notificationResponse.data.order_updates || true,
              eventReminders: notificationResponse.data.event_reminders || true,
              marketingEmails: notificationResponse.data.marketing_emails || false,
              securityAlerts: notificationResponse.data.security_alerts || true,
            })
          }
        } catch (error) {
          console.error('Error fetching profile data:', error)
          setSnackbar({
            open: true,
            message: 'Failed to load profile data',
            severity: 'error'
          })
        } finally {
          setLoading(false)
        }
      }
  
      const handleProfileUpdate = async (field, value) => {
        try {
          const authResponse = await API.getAuth()
          const showrunnerId = authResponse.data.showrunner.id
          
          // Map field names to API field names
          const fieldMapping = {
            firstName: 'first_name',
            lastName: 'last_name',
            email: 'email',
            phone: 'phone',
            company: 'company',
            website: 'website',
            bio: 'bio',
            location: 'location',
          }
          
          const apiField = fieldMapping[field] || field
          const updateData = { [apiField]: value }
          
          await API.updateProfile(showrunnerId, updateData)
          
          const updatedProfile = { ...profile, [field]: value }
          setProfile(updatedProfile)
          
          setSnackbar({
            open: true,
            message: `${field} has been updated successfully.`,
            severity: 'success'
          })
        } catch (error) {
          console.error('Error updating profile:', error)
          setSnackbar({
            open: true,
            message: 'Failed to update profile',
            severity: 'error'
          })
        }
      }
      
      const handleNotificationToggle = async (setting) => {
        try {
          const authResponse = await API.getAuth()
          const showrunnerId = authResponse.data.showrunner.id
          
          const updatedSettings = {
            ...notificationSettings,
            [setting]: !notificationSettings[setting]
          }
          
          // Map setting names to API field names
          const settingMapping = {
            emailNotifications: 'email_notifications',
            smsNotifications: 'sms_notifications',
            orderUpdates: 'order_updates',
            eventReminders: 'event_reminders',
            marketingEmails: 'marketing_emails',
            securityAlerts: 'security_alerts',
          }
          
          const apiSetting = settingMapping[setting] || setting
          const preference = { [apiSetting]: updatedSettings[setting] }
          
          await API.updateTicketSalesNotificationPreferences(showrunnerId, preference)
          
          setNotificationSettings(updatedSettings)
          
          setSnackbar({
            open: true,
            message: `${setting} has been ${updatedSettings[setting] ? 'enabled' : 'disabled'}.`,
            severity: 'success'
          })
        } catch (error) {
          console.error('Error updating notification preferences:', error)
          setSnackbar({
            open: true,
            message: 'Failed to update notification preferences',
            severity: 'error'
          })
        }
      }
  
  const handleAvatarUpload = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Handle file upload logic here
      console.log('Avatar upload:', file)
      setSnackbar({
        open: true,
        message: 'Your profile picture has been updated successfully.',
        severity: 'success'
      })
    }
  }
  
      if (loading) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress size={60} />
          </Box>
        )
      }
      
      return (
        <Box>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
              Profile & Settings
            </Typography>
            <Typography color="grey.600">
              Manage your account information and preferences
            </Typography>
          </Box>
      
      <Grid container spacing={4}>
        {/* Main Profile */}
        <Grid item xs={12} lg={8}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Basic Information */}
            <Card>
              <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h6" fontWeight="600" color="grey.800">Basic Information</Typography>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Save size={16} />}
                        sx={{
                          background: 'linear-gradient(135deg, #0029FF 0%, #4A5BFF 100%)',
                          fontWeight: 600,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #001ECC 0%, #0029FF 100%)',
                          },
                        }}
                      >
                        Save Changes
                      </Button>
                    </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <ProfileField
                    label="First Name"
                    value={profile.firstName}
                    icon={User}
                    isEditable={true}
                    onEdit={(value) => handleProfileUpdate('firstName', value)}
                  />
                  
                  <ProfileField
                    label="Last Name"
                    value={profile.lastName}
                    icon={User}
                    isEditable={true}
                    onEdit={(value) => handleProfileUpdate('lastName', value)}
                  />
                  
                  <ProfileField
                    label="Email"
                    value={profile.email}
                    icon={Mail}
                    type="email"
                    isEditable={true}
                    onEdit={(value) => handleProfileUpdate('email', value)}
                  />
                  
                  <ProfileField
                    label="Phone"
                    value={profile.phone}
                    icon={Phone}
                    type="tel"
                    isEditable={true}
                    onEdit={(value) => handleProfileUpdate('phone', value)}
                  />
                  
                  <ProfileField
                    label="Company"
                    value={profile.company}
                    icon={Building}
                    isEditable={true}
                    onEdit={(value) => handleProfileUpdate('company', value)}
                  />
                  
                  <ProfileField
                    label="Website"
                    value={profile.website}
                    icon={Globe}
                    type="url"
                    isEditable={true}
                    onEdit={(value) => handleProfileUpdate('website', value)}
                  />
                  
                  <ProfileField
                    label="Location"
                    value={profile.location}
                    icon={MapPin}
                    isEditable={true}
                    onEdit={(value) => handleProfileUpdate('location', value)}
                  />
                  
                  <ProfileField
                    label="Bio"
                    value={profile.bio}
                    icon={User}
                    type="textarea"
                    isEditable={true}
                    onEdit={(value) => handleProfileUpdate('bio', value)}
                  />
                </Box>
              </CardContent>
            </Card>
            
            {/* Security Settings */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3 }}>Security</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button variant="outlined" startIcon={<Shield size={16} />}>
                    Change Password
                  </Button>
                  <Button variant="outlined" startIcon={<Shield size={16} />}>
                    Enable Two-Factor Authentication
                  </Button>
                  <Button variant="outlined" startIcon={<Shield size={16} />}>
                    View Login History
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
        
        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Avatar */}
            <Card>
              <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={profile.avatar}
                        sx={{ 
                          width: 100, 
                          height: 100, 
                          fontSize: '2.5rem',
                          fontWeight: 600,
                          background: 'linear-gradient(135deg, #0029FF 0%, #4A5BFF 100%)',
                          border: '4px solid white',
                          boxShadow: '0 8px 32px rgba(0, 41, 255, 0.2)',
                        }}
                      >
                        {profile.firstName?.[0]}{profile.lastName?.[0]}
                      </Avatar>
                      <input
                        type="file"
                        id="avatar-upload"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        style={{ display: 'none' }}
                      />
                      <Button
                        component="label"
                        htmlFor="avatar-upload"
                        variant="outlined"
                        size="small"
                        startIcon={<Camera size={16} />}
                        sx={{
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          '&:hover': {
                            borderColor: 'primary.dark',
                            backgroundColor: 'primary.main',
                            color: 'white',
                          },
                        }}
                      >
                        Change Photo
                      </Button>
                    </Box>
              </CardContent>
            </Card>
            
            {/* Notification Preferences */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3 }}>Notifications</Typography>
                <Box>
                  <NotificationSetting
                    title="Email Notifications"
                    description="Receive updates via email"
                    isEnabled={notificationSettings.emailNotifications}
                    onToggle={() => handleNotificationToggle('emailNotifications')}
                  />
                  <Divider />
                  <NotificationSetting
                    title="SMS Notifications"
                    description="Receive updates via text message"
                    isEnabled={notificationSettings.smsNotifications}
                    onToggle={() => handleNotificationToggle('smsNotifications')}
                  />
                  <Divider />
                  <NotificationSetting
                    title="Order Updates"
                    description="Get notified about order changes"
                    isEnabled={notificationSettings.orderUpdates}
                    onToggle={() => handleNotificationToggle('orderUpdates')}
                  />
                  <Divider />
                  <NotificationSetting
                    title="Event Reminders"
                    description="Receive event reminders"
                    isEnabled={notificationSettings.eventReminders}
                    onToggle={() => handleNotificationToggle('eventReminders')}
                  />
                  <Divider />
                  <NotificationSetting
                    title="Marketing Emails"
                    description="Receive promotional content"
                    isEnabled={notificationSettings.marketingEmails}
                    onToggle={() => handleNotificationToggle('marketingEmails')}
                  />
                  <Divider />
                  <NotificationSetting
                    title="Security Alerts"
                    description="Get notified about security events"
                    isEnabled={notificationSettings.securityAlerts}
                    onToggle={() => handleNotificationToggle('securityAlerts')}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
